import globalCallState from "Stores/GlobalCallState";
import PaywallUtils from "Utils/PaywallUtils";
import { MXP_EVENT } from "Static/MixPanelEvents";
import userDetail from "Stores/userDetail";
import {
  checkTeamSubscriptionActive,
  getFeatureFlagWithTeam
} from "Utils/commonAPI";
import DialerServiceData from "Stores/DialerServiceStore";
import Utils from "Utils/utils";
import { toasterState } from "Components/common/toaster";
import calendarState from "../Pages/Dashboard/settings/Calendar/State";
import { calendarBookMeetingState } from "Components/ContactCard/CalendarBookMeeting/State";

/** Why a rep cannot be sent to the dialer — decides which message they should see. */
export const CALL_GATE = {
  OK: "ok",
  NO_NUMBER: "no_number",
  INDIA_LOOKUP_FAILED: "india_lookup_failed"
};

// Latched only after a lookup that produced a number, so a transient failure cannot
// lock an India-only rep out of the dialer for the rest of the session.
let indiaLookupSucceeded = false;

/**
 * Can this rep place a call at all? The original check only asked whether a Twilio
 * number was configured, which locks out India-only teams: they own no Twilio number,
 * so dialerDefaultNo is "purchaseNumber" and the dialer refuses to open even though
 * they have a working India DID.
 *
 * The VoBiz list is fetched on demand here because nothing else has loaded it yet at
 * this point in the flow.
 */
export const checkCallableNumber = async indiaCallingEnabled => {
  // Keep the original semantics: only an explicit "purchaseNumber" means "none".
  if (DialerServiceData?.dialerDefaultNo !== "purchaseNumber") {
    return CALL_GATE.OK;
  }
  if (!indiaCallingEnabled) {
    return CALL_GATE.NO_NUMBER;
  }
  if (DialerServiceData.voBizDefaultNo === null && !indiaLookupSucceeded) {
    await DialerServiceData.getVoBizPhoneNumbers();
    // getVoBizPhoneNumbers only assigns voBizDefaultNo on SUCCESS, so a still-null
    // value means the call failed rather than the team owning no number.
    if (DialerServiceData.voBizDefaultNo === null) {
      return CALL_GATE.INDIA_LOOKUP_FAILED;
    }
    indiaLookupSucceeded = true;
  }
  const indiaNumber = DialerServiceData.voBizDefaultNo;
  return !!indiaNumber && indiaNumber !== "purchaseNumber"
    ? CALL_GATE.OK
    : CALL_GATE.NO_NUMBER;
};

/**
 * Tells the rep why the dialer did not open. A failed India lookup must not route them
 * to the Twilio purchase flow: they own a number, we simply could not read it, and
 * "buy a number" is both wrong and unactionable for an India-only team.
 */
export const notifyNoCallableNumber = reason => {
  if (reason === CALL_GATE.INDIA_LOOKUP_FAILED) {
    toasterState.setToastMsg(
      "Could not load your calling number. Please try again in a moment.",
      "fail"
    );
    return;
  }
  Utils.showUpgradeSettingsPopup("dialerPurchase", true, true);
};

export const openDialerCard = async (pageType = "calls") => {
  const {
    dialerEnabled = false,
    inFreePlan = false,
    planName = "",
    indiaCallingEnabled = false
  } = userDetail?.userFeatureFlag || {};

  if (!dialerEnabled) {
    PaywallUtils.upgradeUtilsFunction("dialer", false, planName, inFreePlan);
  } else {
    if (await checkTeamSubscriptionActive()) {
      if (dialerEnabled !== "") {
        if (dialerEnabled) {
          const callGate = await checkCallableNumber(indiaCallingEnabled);
          if (callGate === CALL_GATE.OK) {
            Utils.mixpanelTrack(MXP_EVENT.MAKE_CALL_CLICKED, {
              pageType
            });
            await DialerServiceData.getDailerToken();
            userDetail.setUserFeatureFlag(await getFeatureFlagWithTeam());
            DialerServiceData.setDialerCallLogs(false);
            globalCallState.setPopupValues({
              ...globalCallState.popupValues,
              ...{
                pageType,
                isFromContactSection: true
              }
            });
            globalCallState.setShowCallSlider(true);
          } else {
            notifyNoCallableNumber(callGate);
          }
        } else {
          PaywallUtils.upgradeUtilsFunction(
            "callContact",
            false,
            planName,
            inFreePlan
          );
        }
      } else {
        toasterState.setToastMsg("Please wait...", "info");
      }
    }
  }
};

export const openCalendarPopup = async (
  history,
  pageType = "",
  contactInfo = []
) => {
  const {
    inFreePlan = false,
    planName = "",
    calendarIntegrationEnabled = false
  } = userDetail?.userFeatureFlag || {};
  if (Utils.isAccountConnected()) {
    const primaryInbox = userDetail?.connectedInboxes?.[0] || {};
    if (primaryInbox?.type === "smtp") {
      toasterState.setToastMsg(
        "Connect a Google or Microsoft as default inbox to access calendar",
        "fail"
      );
    } else {
      let calendarInboxConnected =
        await calendarState.getCalendarConnnectedAccount();
      if (calendarInboxConnected?.email) {
        if (calendarIntegrationEnabled) {
          calendarBookMeetingState.setPageType(pageType);
          if (contactInfo?.length) {
            calendarBookMeetingState.setContact(contactInfo?.[0]);
          }
          calendarBookMeetingState.setShowPopup(true);
        } else {
          PaywallUtils.upgradeUtilsFunction(
            "calendarUpgrade",
            false,
            planName,
            inFreePlan
          );
        }
      } else {
        toasterState.setToastMsg(
          "Please disconnect your default inbox and reconnect it to access calendar",
          "fail"
        );
      }
    }
  } else {
    Utils.showAddAccountPopup(
      { history },
      "Please connect your email account to access calendar"
    );
  }
};
